import React, { useRef, useState } from 'react';
import { Camera, Trash2, Image as ImageIcon, Loader2 } from 'lucide-react';
import Avatar from './Avatar';
import AvatarCropper from './AvatarCropper';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ProfilePhotoSection({ user, onUpdate }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
    e.target.value = '';
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete('/users/me/avatar');
      toast.success('Foto rimossa');
      onUpdate?.();
    } catch { toast.error('Errore'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="card-surface p-4 space-y-4" data-testid="profile-photo-section">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar username={user?.username} src={user?.avatar_url ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}?auth=${localStorage.getItem('gtc_token')}` : null} size={72} className="border-2 border-[var(--gold)]/40" />
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[var(--gold)] text-black flex items-center justify-center" style={{ boxShadow: '0 4px 12px rgba(212,175,55,0.4)' }}>
            <Camera className="w-3.5 h-3.5" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-medium text-white text-sm">Foto profilo</div>
          <div className="text-[11px] text-white/50 leading-relaxed mt-1">
            La tua foto apparirà nelle chat con l'assistenza e nel pannello Admin.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="photo-gallery-btn"
          onClick={() => fileRef.current?.click()}
          className="py-3 rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-xs text-white/80 flex items-center justify-center gap-2 transition-colors"
        >
          <ImageIcon className="w-4 h-4 text-[var(--gold)]" /> Galleria
        </button>
        <button
          data-testid="photo-camera-btn"
          onClick={() => cameraRef.current?.click()}
          className="py-3 rounded-2xl border border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-xs text-white/80 flex items-center justify-center gap-2 transition-colors"
        >
          <Camera className="w-4 h-4 text-[var(--gold)]" /> Fotocamera
        </button>
      </div>

      {user?.avatar_url && (
        <button
          data-testid="photo-delete-btn"
          onClick={doDelete}
          disabled={deleting}
          className="w-full py-2.5 rounded-2xl text-xs text-red-400/90 hover:text-red-400 border border-red-500/20 hover:border-red-500/40 flex items-center justify-center gap-2 transition-colors"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-3.5 h-3.5" /> Rimuovi foto</>}
        </button>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={onPick} />

      {preview && (
        <AvatarCropper
          imageSrc={preview}
          onClose={() => setPreview(null)}
          onUploaded={() => { setPreview(null); onUpdate?.(); }}
        />
      )}
    </div>
  );
}
